import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button as MuiButton, Box } from '@mui/material';
import { MoveLeft } from 'lucide-react';

interface JapaneseKeyboardProps {
    onKeyPress: (char: string) => void;
    onBackspace: () => void;
    onEnter: () => void;
    isDarkTheme: boolean;
}

type KeyboardLayout = 'hiragana' | 'katakana' | 'numbers';

const JapaneseKeyboard: React.FC<JapaneseKeyboardProps> = ({ onKeyPress, onBackspace, onEnter, isDarkTheme }) => {
    const [layout, setLayout] = useState<KeyboardLayout>('hiragana');

    // Japanese IME style layouts (no romaji, proper kana arrangement)
    const hiraganaLayout = [
        ['あ', 'い', 'う', 'え', 'お', 'や', 'ゆ', 'よ', 'わ', 'ん'],
        ['か', 'き', 'く', 'け', 'こ', 'ら', 'り', 'る', 'れ', 'ろ'],
        ['さ', 'し', 'す', 'せ', 'そ', 'ま', 'み', 'む', 'め', 'も'],
        ['た', 'ち', 'つ', 'て', 'と', 'な', 'に', 'ぬ', 'ね', 'の'],
        ['は', 'ひ', 'ふ', 'へ', 'ほ', 'が', 'ぎ', 'ぐ', 'げ', 'ご'],
        ['ぱ', 'ぴ', 'ぷ', 'ぺ', 'ぽ', 'ざ', 'じ', 'ず', 'ぜ', 'ぞ'],
        ['だ', 'ぢ', 'づ', 'で', 'ど', 'ば', 'び', 'ぶ', 'べ', 'ぼ'],
        ['きゃ', 'きゅ', 'きょ', 'しゃ', 'しゅ', 'しょ', 'ちゃ', 'ちゅ', 'ちょ'],
        ['にゃ', 'にゅ', 'にょ', 'ひゃ', 'ひゅ', 'ひょ', 'みゃ', 'みゅ', 'みょ'],
        ['ぎゃ', 'ぎゅ', 'ぎょ', 'じゃ', 'じゅ', 'じょ', 'びゃ', 'びゅ', 'びょ'],
        ['ぴゃ', 'ぴゅ', 'ぴょ', 'っ', 'ゃ', 'ゅ', 'ょ']
    ];

    const katakanaLayout = [
        ['ア', 'イ', 'ウ', 'エ', 'オ', 'ヤ', 'ユ', 'ヨ', 'ワ', 'ン'],
        ['カ', 'キ', 'ク', 'ケ', 'コ', 'ラ', 'リ', 'ル', 'レ', 'ロ'],
        ['サ', 'シ', 'ス', 'セ', 'ソ', 'マ', 'ミ', 'ム', 'メ', 'モ'],
        ['タ', 'チ', 'ツ', 'テ', 'ト', 'ナ', 'ニ', 'ヌ', 'ネ', 'ノ'],
        ['ハ', 'ヒ', 'フ', 'ヘ', 'ホ', 'ガ', 'ギ', 'グ', 'ゲ', 'ゴ'],
        ['パ', 'ピ', 'プ', 'ペ', 'ポ', 'ザ', 'ジ', 'ズ', 'ゼ', 'ゾ'],
        ['ダ', 'ヂ', 'ヅ', 'デ', 'ド', 'バ', 'ビ', 'ブ', 'ベ', 'ボ'],
        ['キャ', 'キュ', 'キョ', 'シャ', 'シュ', 'ショ', 'チャ', 'チュ', 'チョ'],
        ['ニャ', 'ニュ', 'ニョ', 'ヒャ', 'ヒュ', 'ヒョ', 'ミャ', 'ミュ', 'ミョ'],
        ['ギャ', 'ギュ', 'ギョ', 'ジャ', 'ジュ', 'ジョ', 'ビャ', 'ビュ', 'ビョ'],
        ['ピャ', 'ピュ', 'ピョ', 'ッ', 'ャ', 'ュ', 'ョ']
    ];

    const numbersLayout = [
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
        ['-', '=', '[', ']', '\\', ';', '\'', ',', '.', '/'],
        ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')'],
        ['+', '{', '}', '|', ':', '"', '<', '>', '?'],
        ['~', '`', '_', '€', '£', '¥', '§', '±']
    ];

    const currentLayout = layout === 'hiragana' ? hiraganaLayout : layout === 'katakana' ? katakanaLayout : numbersLayout;

    const keyVariants = {
        rest: { scale: 1 },
        hover: { scale: 1.05 },
        tap: { scale: 0.95 }
    };

    return (
        <Box className={`p-4 rounded-2xl shadow-xl transition-colors duration-300 ${isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-pink-50 border border-pink-100'}`}>
            <Box className="flex justify-between mb-3">
                {['hiragana', 'katakana', 'numbers'].map((type) => (
                    <MuiButton
                        key={type}
                        onClick={() => setLayout(type as KeyboardLayout)}
                        variant={layout === type ? 'contained' : 'outlined'}
                        size="small"
                        className={`${layout === type ? '!bg-pink-500 !text-white' : '!border-pink-300 !text-pink-500'} !rounded-lg !font-bold !text-sm`}
                    >
                        {type === 'hiragana' ? 'あ' : type === 'katakana' ? 'ア' : '123'}
                    </MuiButton>
                ))}
            </Box>

            <AnimatePresence mode="wait">
                <motion.div
                    key={layout}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-10 gap-2"
                >
                    {currentLayout.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                            {row.map((char, charIndex) => (
                                <motion.div
                                    key={`${rowIndex}-${charIndex}-${char}`}
                                    variants={keyVariants}
                                    whileHover="hover"
                                    whileTap="tap"
                                    className="col-span-1"
                                >
                                    <MuiButton
                                        onClick={() => onKeyPress(char)}
                                        variant="outlined"
                                        className={`w-full h-10 sm:h-12 !min-w-0 !p-0 !text-lg !font-bold !rounded-md ${isDarkTheme ? '!border-gray-600 !text-gray-100 hover:!bg-gray-700' : '!border-pink-200 !text-pink-700 hover:!bg-pink-100'}`}
                                    >
                                        {char}
                                    </MuiButton>
                                </motion.div>
                            ))}
                        </React.Fragment>
                    ))}
                </motion.div>
            </AnimatePresence>

            <Box className="flex gap-2 mt-3">
                <motion.div variants={keyVariants} whileHover="hover" whileTap="tap" className="flex-1">
                    <MuiButton
                        onClick={() => onKeyPress(' ')}
                        variant="outlined"
                        className={`w-full h-10 sm:h-12 !min-w-0 !text-lg !font-bold !rounded-md ${isDarkTheme ? '!border-gray-600 !text-gray-100 hover:!bg-gray-700' : '!border-pink-200 !text-pink-700 hover:!bg-pink-100'}`}
                    >
                        Space
                    </MuiButton>
                </motion.div>
                <motion.div variants={keyVariants} whileHover="hover" whileTap="tap">
                    <MuiButton
                        onClick={onBackspace}
                        variant="outlined"
                        className={`w-16 h-10 sm:h-12 !min-w-0 !rounded-md ${isDarkTheme ? '!border-gray-600 !text-gray-100 hover:!bg-gray-700' : '!border-pink-200 !text-pink-700 hover:!bg-pink-100'}`}
                    >
                        <MoveLeft size={20} />
                    </MuiButton>
                </motion.div>
                <motion.div variants={keyVariants} whileHover="hover" whileTap="tap">
                    <MuiButton
                        onClick={onEnter}
                        variant="contained"
                        className={`w-16 h-10 sm:h-12 !min-w-0 !rounded-md !bg-pink-500 !text-white hover:!bg-pink-600`}
                    >
                        Enter
                    </MuiButton>
                </motion.div>
            </Box>
        </Box>
    );
};

export default JapaneseKeyboard;
